import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createAuth0 } from '@auth0/auth0-vue'
import * as Sentry from '@sentry/vue'
import { initAnalytics } from './services/analytics'
import { setSweepTokenProvider } from './services/sweep/token'
import './style.css'
import App from './App.vue'
import router from './router'

const app = createApp(App)

// Initialize Sentry (no-ops silently if DSN is not set)
Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'dev',
    enabled: !!import.meta.env.VITE_SENTRY_DSN,
    integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
        }),
    ],
    // Performance monitoring (off by default, increase in production)
    tracesSampleRate: 0,
    // Session replay: capture replays only when an error occurs
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // Don't send PII by default
    sendDefaultPii: false,
    // Ignore common non-actionable errors
    ignoreErrors: [
        'Login required',
        'Consent required',
        'Missing Refresh Token',
        'Network Error',
        'Failed to fetch',
        'Load failed',
    ],
    denyUrls: [
        /auth0\.com/i,
    ],
})

initAnalytics()

app.use(createPinia())
app.use(router)

const auth0 = createAuth0({
    domain: import.meta.env.VITE_AUTH0_DOMAIN,
    clientId: import.meta.env.VITE_AUTH0_CLIENT_ID,
    authorizationParams: {
        redirect_uri: `${window.location.origin}/auth/callback`,
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
    },
})
app.use(auth0)

// Sweep's API client attaches bearers from an axios interceptor, which has no
// component context and so cannot use the useAuth() composable. Ask Auth0 only
// when it actually has a session: calling getAccessTokenSilently() without one
// starts a silent-auth round trip that takes seconds to fail, which would stall
// every Sweep request behind it on a dev-login session.
setSweepTokenProvider(() =>
    auth0.isAuthenticated.value ? auth0.getAccessTokenSilently() : Promise.resolve(null)
)

app.mount('#app')
